import {
  ArrowUpRight,
  Mic,
  Sparkles,
  Star,
  Subtitles,
} from 'lucide-react';
import type { ReactElement } from 'react';

import Image from '@/components/ui/image';
import type { Anime } from '@/lib/yozora/types';
import { cn } from '@/lib/utils';

interface BestMatchCardProps {
  anime: Anime;
  /**
   * Compact mode (used in the command palette dialog): poster sits at
   * ~80px on a single line. The default (search page) uses a much
   * larger poster + bigger type.
   */
  size?: 'sm' | 'md';
  /** Click handler — receives the anime id. Defaults to navigating
   *  via plain anchor href if omitted. */
  onSelect?: (id: number) => void;
  active?: boolean;
  onHover?: () => void;
  className?: string;
}

function pickTitle(anime: Anime): string {
  return (
    anime.title?.english ||
    anime.title?.romaji ||
    anime.title?.native ||
    'Untitled'
  );
}

function pickCover(anime: Anime): string {
  return (
    anime.poster?.large ||
    anime.poster?.medium ||
    anime.poster?.small ||
    '/png/thumbnail.png'
  );
}

function shortRating(anime: Anime): string | null {
  const raw = anime.age_rating?.rating ?? '';
  const trimmed = raw.split('-')[0]?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

export function BestMatchCard({
  anime,
  size = 'md',
  onSelect,
  active = false,
  onHover,
  className,
}: BestMatchCardProps): ReactElement {
  const title = pickTitle(anime);
  const cover = pickCover(anime);
  const score = anime.score ? (anime.score / 10).toFixed(1) : null;
  const rating = shortRating(anime);
  const format = anime.format;
  const year = anime.season_year;
  const subCount = anime.sub_count ?? 0;
  const dubCount = anime.dub_count ?? 0;

  const sizing =
    size === 'sm'
      ? {
          poster: 'my-3 ml-3 h-24 w-[68px] sm:h-28 sm:w-20',
          content: 'gap-2 p-3 sm:p-4',
          title: 'text-base sm:text-lg',
          metaText: 'text-[11px]',
          starIcon: 'h-3 w-3',
          audioIcon: 'h-3 w-3',
          cta: 'right-3 top-3 px-2 py-1 text-[10px]',
          chevron: 'h-3 w-3',
        }
      : {
          poster: 'my-4 ml-4 h-32 w-[92px] sm:h-40 sm:w-28',
          content: 'gap-2 p-4 sm:p-6',
          title: 'text-lg sm:text-2xl',
          metaText: 'text-xs',
          starIcon: 'h-3.5 w-3.5',
          audioIcon: 'h-3.5 w-3.5',
          cta: 'right-4 top-4 px-3 py-1.5 text-xs',
          chevron: 'h-3.5 w-3.5',
        };

  const inner = (
    <>
      {/* Gradient-washed cover backdrop */}
      <div aria-hidden="true" className="absolute inset-0 -z-10">
        <Image
          src={cover}
          alt=""
          fill
          sizes="(max-width: 768px) 100vw, 800px"
          className="object-cover opacity-50"
          style={{ objectPosition: '50% 20%' }}
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-transparent to-transparent" />
      </div>

      {/* Sharp poster panel */}
      <div
        className={cn(
          'relative flex-shrink-0 overflow-hidden rounded-xl shadow-lg ring-1 ring-border/60',
          sizing.poster
        )}
      >
        <Image
          src={cover}
          alt={title}
          fill
          sizes={size === 'sm' ? '80px' : '112px'}
          className="object-cover"
          loading="eager"
        />
      </div>

      {/* Content */}
      <div className={cn('flex min-w-0 flex-1 flex-col justify-center', sizing.content)}>
        <span className="inline-flex w-fit items-center gap-1 rounded-full border border-primary/30 bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
          <Sparkles className="h-3 w-3 fill-primary" />
          Best match
        </span>

        <h3
          className={cn(
            'line-clamp-2 font-bold leading-tight text-foreground drop-shadow-sm',
            sizing.title
          )}
        >
          {title}
        </h3>

        <div
          className={cn(
            'flex flex-wrap items-center gap-x-2.5 gap-y-1 text-muted-foreground',
            sizing.metaText
          )}
        >
          {score && (
            <span className="flex items-center gap-0.5 font-semibold text-yellow-500">
              <Star className={cn(sizing.starIcon, 'fill-yellow-500 text-yellow-500')} />
              <span className="tabular-nums">{score}</span>
            </span>
          )}
          {rating && (
            <span className="rounded border border-border/70 bg-background/70 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-foreground/80">
              {rating}
            </span>
          )}
          {format && <span className="text-foreground/80">{format}</span>}
          {year && (
            <span className="tabular-nums text-foreground/80">{year}</span>
          )}
          {subCount > 0 && (
            <span className="inline-flex items-center gap-0.5 tabular-nums text-foreground/80">
              <Subtitles className={sizing.audioIcon} />
              {subCount}
            </span>
          )}
          {dubCount > 0 && (
            <span className="inline-flex items-center gap-0.5 tabular-nums text-foreground/80">
              <Mic className={sizing.audioIcon} />
              {dubCount}
            </span>
          )}
        </div>
      </div>

      {/* CTA chip */}
      <span
        className={cn(
          'absolute inline-flex items-center gap-1 rounded-full border font-semibold transition-colors',
          sizing.cta,
          active
            ? 'border-primary/60 bg-primary text-primary-foreground'
            : 'border-border/60 bg-background/80 text-foreground group-hover:border-primary/50 group-hover:bg-primary group-hover:text-primary-foreground'
        )}
      >
        Open
        <ArrowUpRight className={sizing.chevron} />
      </span>
    </>
  );

  const sharedClass = cn(
    'group relative isolate flex w-full overflow-hidden rounded-2xl text-left',
    'border bg-card/80 shadow-sm transition-colors duration-200',
    active ? 'border-primary/60' : 'border-border/60 hover:border-primary/50',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
    className
  );

  if (onSelect) {
    return (
      <button
        type="button"
        data-active={active}
        onClick={() => onSelect(anime.id)}
        onMouseMove={onHover}
        className={sharedClass}
      >
        {inner}
      </button>
    );
  }

  return (
    <a
      href={`/anime/${anime.id}`}
      data-active={active}
      onMouseMove={onHover}
      className={sharedClass}
    >
      {inner}
    </a>
  );
}

export default BestMatchCard;
